/**
 * Indian PIN-code → city / state / country resolver for the
 * storefront checkout. Hits the free, no-auth endpoint at
 * `https://api.postalpincode.in/pincode/{pin}`. Multiple post offices
 * map to a single PIN; we take the first one's District/State as the
 * canonical "city, state" pair.
 *
 * Result is module-cached so repeated lookups (e.g. typing 6 digits
 * back-and-forth) don't spam the upstream. Fails closed — if the
 * network is down or the PIN is invalid, returns null and the
 * caller leaves the city/state inputs untouched.
 *
 * Mirrors the panel-side helper at
 * `apps/panel/src/utils/pincodeLookup.ts` but returns plain readable
 * names (the panel translates state to GST 2-digit codes — the
 * storefront just wants "Maharashtra").
 */

export interface PincodeResolution {
  city: string;
  state: string;
  country: string;
}

const cache = new Map<string, PincodeResolution | null>();

interface PostOffice {
  Name?: string;
  District?: string;
  State?: string;
  Country?: string;
}

interface PincodeResponse {
  Status?: string;
  PostOffice?: PostOffice[] | null;
  Message?: string;
}

export async function lookupPincode(pin: string): Promise<PincodeResolution | null> {
  const trimmed = (pin ?? '').trim();
  if (!/^\d{6}$/.test(trimmed)) return null;
  if (cache.has(trimmed)) return cache.get(trimmed)!;
  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 5000);
    const r = await fetch(`https://api.postalpincode.in/pincode/${trimmed}`, {
      signal: ctl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      cache.set(trimmed, null);
      return null;
    }
    const json = (await r.json()) as PincodeResponse[];
    const first = Array.isArray(json) ? json[0] : null;
    const po = first?.PostOffice?.[0];
    if (first?.Status !== 'Success' || !po) {
      cache.set(trimmed, null);
      return null;
    }
    const resolution: PincodeResolution = {
      city: po.District ?? '',
      state: po.State ?? '',
      country: po.Country ?? 'India',
    };
    cache.set(trimmed, resolution);
    return resolution;
  } catch {
    return null;
  }
}
